import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { securityCategoryColor } from '../../charts/securityCategories.js';
/**
 * What a widget LOOKS like, drawn at thumbnail size for the add-widget picker.
 *
 * WHY THIS EXISTS
 * ---------------
 * The picker was a title and one line of prose per widget — 34 of them. "Which
 * security categories they pay for, and who owns each one" and "The 14 security
 * categories as a wheel" describe the same subject, and nothing in either line
 * tells you that the first is a grid you read across and the second is a ring
 * you read at a glance. The only way to find out was to add the widget, look at
 * it, and remove it again. A reader deciding whether they want a card is asking
 * a question about FORM, and prose is the wrong medium for that answer.
 *
 * WHY DRAWINGS AND NOT SCREENSHOTS
 * --------------------------------
 * A screenshot is of one account on one day. It goes stale the first time a
 * widget changes, it carries a real agency's numbers into a menu, and 34 PNGs
 * is 34 files nobody re-captures. These are schematics: the widget's real form,
 * with example marks. They cost nothing to ship (inline SVG, no requests), they
 * inherit the viewer's theme through the same tokens the board uses, and they
 * cannot show data the viewer is not entitled to.
 *
 * THE HONESTY RULE
 * ----------------
 * A preview may simplify; it may not lie about the form. If the widget draws a
 * ring, the preview draws a ring. If it is a list of text rows, the preview is
 * a list of text rows, and it must LOOK like one — a picker that dresses up a
 * text list as a chart sells a card that disappoints on arrival, which is
 * exactly the complaint this board started from. Where a widget carries a
 * meaning in colour (security categories, overdue red), the preview uses that
 * same colour, so the legend a reader learns here is the one they meet on the
 * board.
 *
 * The numbers are invented and deliberately unlabelled: no axis values, no
 * dollar figures. A thumbnail with plausible-looking numbers in it reads as
 * this account's data, and would be believed.
 */
/* ---- palette -------------------------------------------------------------
 * Every colour is a board token with a literal fallback, so a thumbnail
 * rendered inside a shell that has not loaded the board stylesheet still draws
 * something legible rather than black-on-black.
 */
const ACCENT = 'var(--accent, #3b82f6)';
const HAIR = 'var(--hair, var(--border, #e5e7eb))';
/** Stands in for text. Never a value — a grey bar here means "words go here". */
const TEXT = 'var(--border, #e5e7eb)';
const CRIT = 'var(--crit, #dc2626)';
const WARN = 'var(--warn, #d97706)';
const GOOD = 'var(--good, #16a34a)';
const cat = securityCategoryColor;
/** The drawing area. Everything below is authored against this box. */
const VB = { w: 120, h: 72 };
/* ---- primitives ---------------------------------------------------------- */
/** A row of grey bars standing in for a line of text. */
function textLine(x, y, w, h = 4, fill = TEXT) {
    return _jsx("rect", { x: x, y: y, width: w, height: h, rx: h / 2, fill: fill });
}
/** Vertical bars on a baseline — a fiscal-year trajectory. */
const bars = () => {
    const h = [13, 19, 16, 29, 45];
    return (_jsxs(_Fragment, { children: [_jsx("line", { x1: 6, y1: 63, x2: 114, y2: 63, stroke: HAIR, strokeWidth: 1 }), h.map((v, i) => (_jsx("rect", { x: 10 + i * 21, y: 63 - v, width: 14, height: v, fill: ACCENT, opacity: i === h.length - 1 ? 1 : 0.42 }, i)))] }));
};
/**
 * A category × year grid. Row labels are drawn as stubs because the real
 * heatmaps carry them, and a grid without labels reads as decoration.
 */
const matrix = () => {
    const rows = ['NGFW/Firewall', 'SSE/SASE', 'Endpoint/EDR/XDR'];
    const v = [
        [0.85, 0, 0.5, 0.2, 0.35],
        [0, 0.25, 0.7, 1, 0.12],
        [0.15, 0, 0, 0.4, 0.6],
    ];
    return (_jsx(_Fragment, { children: rows.map((r, ri) => (_jsxs("g", { children: [textLine(6, 18 + ri * 18, 22, 4), v[ri].map((intensity, ci) => (_jsx("rect", { x: 33 + ci * 17, y: 12 + ri * 18, width: 15, height: 15, fill: intensity ? cat(r) : HAIR, opacity: intensity ? 0.25 + intensity * 0.75 : 1 }, ci)))] }, r))) }));
};
/**
 * The coverage wheel. Segments are coloured by STATE, not by category, because
 * that is what the real ring does — the question it answers is "who holds this",
 * and the open arc has to be visibly open. Grouped the way the chart groups
 * them, so the thumbnail shows the one reading the widget is for.
 */
const ring = () => {
    const cx = 60;
    const cy = 36;
    const r = 22;
    const C = 2 * Math.PI * r;
    const segs = [
        // [colour, share, opacity] — ours, then competitor-held, then open.
        [ACCENT, 0.23, 1],
        ['var(--muted, #6b7280)', 0.46, 0.6],
        [WARN, 0.31, 0.28],
    ];
    let acc = 0;
    return (_jsx("g", { transform: `rotate(-90 ${cx} ${cy})`, children: segs.map(([color, f, o]) => {
            const el = (_jsx("circle", { cx: cx, cy: cy, r: r, fill: "none", stroke: color, strokeOpacity: o, strokeWidth: 11, strokeDasharray: `${Math.max(f * C - 1.5, 1)} ${C}`, strokeDashoffset: -acc * C }, color));
            acc += f;
            return el;
        }) }));
};
/** Countdown rings — solicitations, where the clock is the mark. */
const clocks = () => {
    const dials = [
        [24, 0.82, CRIT],
        [60, 0.45, WARN],
        [96, 0.18, ACCENT],
    ];
    const r = 13;
    const C = 2 * Math.PI * r;
    return (_jsx(_Fragment, { children: dials.map(([cx, f, color]) => (_jsxs("g", { children: [_jsx("circle", { cx: cx, cy: 30, r: r, fill: "none", stroke: HAIR, strokeWidth: 5 }), _jsx("circle", { cx: cx, cy: 30, r: r, fill: "none", stroke: color, strokeWidth: 5, strokeDasharray: `${f * C} ${C}`, transform: `rotate(-90 ${cx} 30)` }), textLine(cx - 13, 54, 26, 4)] }, cx))) }));
};
/** Rank lines crossing — the bump chart behind "gaining and losing". */
const bump = () => {
    const lines = [
        [ACCENT, [16, 22, 14, 10]],
        [cat('SIEM'), [30, 16, 26, 40]],
        [cat('Data/DLP'), [48, 44, 50, 26]],
    ];
    const x = [14, 46, 78, 108];
    return (_jsx(_Fragment, { children: lines.map(([color, ys]) => (_jsxs("g", { children: [_jsx("polyline", { points: ys.map((y, i) => `${x[i]},${y + 8}`).join(' '), fill: "none", stroke: color, strokeWidth: 2.5, strokeLinejoin: "round", strokeLinecap: "round" }), ys.map((y, i) => (_jsx("circle", { cx: x[i], cy: y + 8, r: 2.5, fill: color }, i)))] }, color))) }));
};
/** Dated pips along one axis — the renewal runway. */
const timeline = () => {
    const pips = [
        [16, 14, CRIT],
        [38, 9, WARN],
        [58, 18, ACCENT],
        [82, 7, ACCENT],
        [104, 12, GOOD],
    ];
    return (_jsxs(_Fragment, { children: [_jsx("line", { x1: 6, y1: 44, x2: 114, y2: 44, stroke: HAIR, strokeWidth: 1.5 }), pips.map(([x, h, color]) => (_jsxs("g", { children: [_jsx("line", { x1: x, y1: 44, x2: x, y2: 44 - h - 6, stroke: color, strokeWidth: 1.5 }), _jsx("circle", { cx: x, cy: 44 - h - 8, r: Math.max(h / 3, 3), fill: color })] }, x))), textLine(6, 56, 20, 4), textLine(48, 56, 20, 4), textLine(94, 56, 20, 4)] }));
};
/** Big-number tiles, each with its trend — the Key numbers card. */
const tiles = () => {
    const spark = [
        '4,12 12,9 20,11 28,4',
        '4,6 12,10 20,7 28,12',
        '4,11 12,8 20,9 28,5',
    ];
    const tone = [ACCENT, CRIT, GOOD];
    return (_jsx(_Fragment, { children: [0, 1, 2].map((i) => (_jsxs("g", { transform: `translate(${6 + i * 37} 14)`, children: [_jsx("rect", { width: 33, height: 44, fill: HAIR, opacity: 0.5 }), textLine(5, 6, 14, 3), _jsx("rect", { x: 5, y: 13, width: 22, height: 9, fill: ACCENT, opacity: 0.85 }), _jsx("polyline", { points: spark[i], transform: "translate(1 26)", fill: "none", stroke: tone[i], strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" })] }, i))) }));
};
/** Mark, name and facts — the identity band. */
const ident = () => (_jsxs(_Fragment, { children: [_jsx("rect", { x: 6, y: 12, width: 26, height: 26, fill: ACCENT, opacity: 0.18 }), _jsx("circle", { cx: 19, cy: 25, r: 7, fill: ACCENT, opacity: 0.55 }), textLine(38, 14, 56, 6), textLine(38, 25, 34, 4), [0, 1, 2, 3].map((i) => (_jsx("rect", { x: 6 + i * 28, y: 46, width: 25, height: 13, fill: "none", stroke: HAIR, strokeWidth: 1.5 }, i)))] }));
/** A ceiling with buys against it — purchasing authority. */
const ceiling = () => (_jsxs(_Fragment, { children: [_jsx("rect", { x: 6, y: 22, width: 108, height: 12, fill: HAIR }), _jsx("rect", { x: 6, y: 22, width: 62, height: 12, fill: ACCENT }), _jsx("line", { x1: 68, y1: 16, x2: 68, y2: 40, stroke: CRIT, strokeWidth: 2 }), [78, 92, 104].map((x) => (_jsx("circle", { cx: x, cy: 28, r: 3.5, fill: CRIT, opacity: 0.8 }, x))), textLine(6, 48, 40, 4), textLine(74, 48, 40, 4)] }));
/** A single score on a dial — health, Zero Trust posture. */
const dial = () => {
    const r = 26;
    const C = Math.PI * r;
    return (_jsxs(_Fragment, { children: [_jsx("path", { d: `M ${60 - r} 50 A ${r} ${r} 0 0 1 ${60 + r} 50`, fill: "none", stroke: HAIR, strokeWidth: 9 }), _jsx("path", { d: `M ${60 - r} 50 A ${r} ${r} 0 0 1 ${60 + r} 50`, fill: "none", stroke: ACCENT, strokeWidth: 9, strokeDasharray: `${C * 0.62} ${C}` }), _jsx("rect", { x: 48, y: 36, width: 24, height: 10, fill: ACCENT, opacity: 0.85 }), textLine(40, 60, 40, 4)] }));
};
/** Boxes and connectors — the org chart. */
const tree = () => (_jsxs(_Fragment, { children: [_jsx("rect", { x: 44, y: 8, width: 32, height: 13, fill: ACCENT, opacity: 0.8 }), _jsx("path", { d: "M60 21 V30 M22 30 H98 M22 30 V38 M60 30 V38 M98 30 V38", fill: "none", stroke: HAIR, strokeWidth: 1.5 }), [6, 44, 82].map((x) => (_jsx("rect", { x: x, y: 38, width: 32, height: 13, fill: HAIR }, x))), _jsx("path", { d: "M22 51 V58 M22 58 H38", fill: "none", stroke: HAIR, strokeWidth: 1.5 }), _jsx("rect", { x: 38, y: 52, width: 26, height: 11, fill: HAIR, opacity: 0.6 })] }));
/**
 * A list of text rows. `lead` is what sits at the left of each row, and it is
 * the thing that distinguishes four otherwise-identical list widgets: a date
 * pip, an avatar, or an overdue flag.
 */
function rows(lead) {
    return () => {
        const y = [10, 24, 38, 52];
        const w = [70, 58, 76, 50];
        const tone = [CRIT, WARN, TEXT, TEXT];
        return (_jsx(_Fragment, { children: y.map((yy, i) => (_jsxs("g", { children: [lead === 'avatar' && _jsx("circle", { cx: 11, cy: yy + 5, r: 5, fill: HAIR }), lead === 'dot' && _jsx("circle", { cx: 9, cy: yy + 5, r: 3, fill: ACCENT, opacity: 0.7 }), lead === 'due' && _jsx("rect", { x: 6, y: yy, width: 3, height: 11, fill: tone[i] }), textLine(lead === 'none' ? 6 : 20, yy + 1, w[i], 5), textLine(lead === 'none' ? 6 : 20, yy + 9, Math.round(w[i] * 0.5), 3), lead === 'due' && i < 2 && (_jsx("rect", { x: 96, y: yy + 1, width: 18, height: 7, rx: 3.5, fill: tone[i], opacity: 0.75 }))] }, yy))) }));
    };
}
/**
 * A ranked list where each row carries a bar. `split` draws the cyber share as
 * a darker head inside the bar (purchase history); `stacked` splits the bar
 * into category segments (partners, share of wallet).
 */
/**
 * A search field over avatar rows — the contacts DIRECTORY, as against
 * `contacts`, which is the same people with no way to find one.
 *
 * The field is what distinguishes the two thumbnails in the picker, so it
 * leads and the rows below it are shortened to make room rather than the field
 * being tucked into a corner where it reads as decoration.
 */
const searchRows = () => {
    const y = [26, 41, 56];
    const w = [62, 52, 68];
    return (_jsxs(_Fragment, { children: [_jsx("rect", { x: 6, y: 6, width: 108, height: 13, rx: 6.5, fill: "none", stroke: HAIR, strokeWidth: 1.5 }), _jsx("circle", { cx: 15, cy: 12.5, r: 3.2, fill: "none", stroke: ACCENT, strokeWidth: 1.5 }), _jsx("line", { x1: 17.4, y1: 14.9, x2: 19.6, y2: 17.1, stroke: ACCENT, strokeWidth: 1.5, strokeLinecap: "round" }), textLine(24, 10.5, 34, 4), y.map((yy, i) => (_jsxs("g", { children: [_jsx("circle", { cx: 11, cy: yy + 5, r: 5, fill: HAIR }), textLine(20, yy + 1, w[i], 5), textLine(20, yy + 9, Math.round(w[i] * 0.5), 3)] }, yy)))] }));
};
/** Two series on one baseline — our bars against a competitor's. */
const versus = () => {
    const ours = [16, 24, 21, 34];
    const theirs = [30, 22, 36, 27];
    return (_jsxs(_Fragment, { children: [ours.map((h, i) => {
                const x = 10 + i * 27;
                return (_jsxs("g", { children: [_jsx("rect", { x: x, y: 58 - h, width: 10, height: h, rx: 1.5, fill: ACCENT }), _jsx("rect", { x: x + 11, y: 58 - theirs[i], width: 10, height: theirs[i], rx: 1.5, fill: HAIR })] }, x));
            }), _jsx("line", { x1: 6, y1: 58, x2: 114, y2: 58, stroke: HAIR, strokeWidth: 1 })] }));
};
function barRows({ split = false, stacked = false } = {}) {
    return () => {
        const y = [10, 24, 38, 52];
        const len = [78, 56, 38, 22];
        const segs = [cat('NGFW/Firewall'), cat('SSE/SASE'), cat('SIEM')];
        return (_jsx(_Fragment, { children: y.map((yy, i) => (_jsxs("g", { children: [textLine(6, yy + 3, 26, 5), stacked ? (segs.map((c, si) => {
                        const parts = [0.5, 0.3, 0.2];
                        const before = parts.slice(0, si).reduce((a, b) => a + b, 0);
                        return (_jsx("rect", { x: 36 + len[i] * before, y: yy + 1, width: Math.max(len[i] * parts[si] - 1, 1), height: 9, fill: c }, c));
                    })) : (_jsxs(_Fragment, { children: [_jsx("rect", { x: 36, y: yy + 1, width: len[i], height: 9, fill: ACCENT, opacity: 0.35 }), split && (_jsx("rect", { x: 36, y: yy + 1, width: len[i] * (i === 1 ? 1 : 0.34), height: 9, fill: i === 3 ? '#94a3b8' : cat(i === 1 ? 'SIEM' : 'NGFW/Firewall') }))] }))] }, yy))) }));
    };
}
/** Article cards with a picture — the news feed. */
const feed = () => (_jsx(_Fragment, { children: [8, 40].map((y) => (_jsxs("g", { children: [_jsx("rect", { x: 6, y: y, width: 26, height: 24, fill: ACCENT, opacity: 0.16 }), _jsx("path", { d: `M8 ${y + 24} l7 -9 l5 6 l4 -5 l6 8 z`, fill: ACCENT, opacity: 0.45 }), textLine(38, y + 1, 74, 5), textLine(38, y + 10, 62, 4), textLine(38, y + 18, 30, 3)] }, y))) }));
/** Nodes and links on a canvas — the tech map. */
const canvas = () => {
    const nodes = [
        [24, 18],
        [62, 12],
        [96, 30],
        [34, 50],
        [74, 54],
    ];
    return (_jsxs(_Fragment, { children: [_jsx("path", { d: "M24 18 L62 12 M62 12 L96 30 M24 18 L34 50 M34 50 L74 54 M74 54 L96 30", fill: "none", stroke: HAIR, strokeWidth: 1.5 }), nodes.map(([x, y], i) => (_jsx("rect", { x: x - 11, y: y - 6, width: 22, height: 12, fill: i % 2 ? ACCENT : HAIR, opacity: i % 2 ? 0.7 : 1 }, i)))] }));
};
/** Four tiers — the value pyramid. */
const pyramid = () => {
    const tiers = [0, 1, 2, 3];
    return (_jsx(_Fragment, { children: tiers.map((i) => {
            const halfTop = 8 + i * 14;
            const halfBottom = 8 + (i + 1) * 14;
            const yTop = 10 + i * 13;
            const yBottom = yTop + 12;
            return (_jsx("path", { d: `M ${60 - halfTop} ${yTop} H ${60 + halfTop} L ${60 + halfBottom} ${yBottom} H ${60 - halfBottom} Z`, fill: ACCENT, opacity: 0.85 - i * 0.17 }, i));
        }) }));
};
/** Questions to ask — prose, but each line is a prompt with a marker. */
const quotes = () => {
    const y = [10, 28, 46];
    const w = [86, 70, 78];
    return (_jsx(_Fragment, { children: y.map((yy, i) => (_jsxs("g", { children: [_jsx("circle", { cx: 11, cy: yy + 6, r: 6, fill: ACCENT, opacity: 0.18 }), _jsx("text", { x: 11, y: yy + 9, textAnchor: "middle", fontSize: 9, fontWeight: 700, fill: ACCENT, fontFamily: "var(--font-sans, system-ui, sans-serif)", children: "?" }), textLine(22, yy + 1, w[i], 5), textLine(22, yy + 9, Math.round(w[i] * 0.6), 3)] }, yy))) }));
};
/** A widget type this build has no drawing for. Says so, rather than lying. */
/** Pins on a street grid — every site this account operates. One is amber:
 *  the office with no address on record, which is what the tile counts. */
const pins = () => {
    const spots = [
        [28, 30, ACCENT],
        [64, 20, ACCENT],
        [94, 42, WARN],
    ];
    return (_jsxs(_Fragment, { children: [[20, 38, 56].map((y) => (_jsx("line", { x1: 6, y1: y, x2: 114, y2: y, stroke: HAIR, strokeWidth: 1 }, y))), [44, 82].map((x) => (_jsx("line", { x1: x, y1: 8, x2: x, y2: 64, stroke: HAIR, strokeWidth: 1 }, x))), spots.map(([cx, cy, color]) => (_jsxs("g", { children: [_jsx("path", { d: `M${cx} ${cy + 12} L${cx - 6} ${cy} A6 6 0 1 1 ${cx + 6} ${cy} Z`, fill: color, opacity: 0.9 }), _jsx("circle", { cx: cx, cy: cy, r: 2.4, fill: "#fff" })] }, cx)))] }));
};
const unknown = () => (_jsxs(_Fragment, { children: [_jsx("rect", { x: 8, y: 10, width: 104, height: 52, fill: "none", stroke: HAIR, strokeWidth: 1.5, strokeDasharray: "4 4" }), textLine(34, 32, 52, 5)] }));
/* ---- which widget draws what ---------------------------------------------
 * Every catalog type appears here, including the ones the OTHER shell owns —
 * the picker is shared, and a GAS-only widget still needs a thumbnail when a
 * GAS user opens it. `boardPreviews.test.js` fails if the two lists drift.
 */
const PREVIEWS = {
    // Account
    identity: ident,
    stats: tiles,
    'purchasing-authority': ceiling,
    locations: pins,
    // People
    'org-chart': tree,
    contacts: rows('avatar'),
    'contacts-directory': searchRows,
    'contact-inbox': rows('avatar'),
    // Money
    renewals: rows('due'),
    'renewal-runway': timeline,
    'share-of-wallet': barRows({ stacked: true }),
    'buying-rhythm': matrix,
    'spend-trajectory': bars,
    'pan-trajectory': bars,
    'panw-heatmap': matrix,
    'procurement-summary': barRows(),
    'purchase-history': barRows({ split: true }),
    competitive: bump,
    // Ours against theirs: two bar series on one baseline, the tab's own subject.
    'competitive-footprint': versus,
    'security-coverage': matrix,
    'coverage-ring': ring,
    partners: barRows({ stacked: true }),
    'partner-involvement': barRows({ split: true, stacked: true }),
    'tech-map': canvas,
    solicitations: clocks,
    bcps: rows('dot'),
    'zero-trust': dial,
    // Narrative
    news: feed,
    notes: rows('none'),
    'business-drivers': rows('dot'),
    funding: rows('dot'),
    'value-pyramid': pyramid,
    'trap-questions': quotes,
    // Motion
    tasks: rows('due'),
    opportunities: barRows(),
    meetings: rows('dot'),
    'meeting-notes': rows('dot'),
    'plaud-recordings': rows('dot'),
    'deal-signals': rows('due'),
    'health-score': dial,
    'proactive-questions': quotes,
    commitments: rows('due'),
};
/** Types with a drawing. Exported so a test can hold it against the catalog. */
export const PREVIEW_TYPES = Object.keys(PREVIEWS);
/**
 * The thumbnail. Decorative by design — `aria-hidden`, because the title and
 * blurb beside it already say what this is, and "chart of five bars" read out
 * loud helps nobody choose a widget.
 */
export function WidgetPreview({ type, className }) {
    const draw = PREVIEWS[type] || unknown;
    return (_jsx("svg", { className: className, viewBox: `0 0 ${VB.w} ${VB.h}`, preserveAspectRatio: "xMidYMid meet", "aria-hidden": "true", focusable: "false", children: draw() }));
}
